import { useQuery } from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet } from "../../api/client";
import { OutputRender } from "../review/OutputRender";
import { MarkdownText, hasMarkdown } from "../review/MarkdownText";

interface Attachment {
  id: string;
  kind: "url" | "file";
  url: string | null;
  filename: string | null;
}

interface UpstreamStep {
  id: string;
  ord: number;
  type: string;
  title: string;
  state: string;
  output: unknown;
  successCriteria: string;
  attachments: Attachment[];
}

interface CommentRow {
  id: string;
  authorUserId: string;
  authorLabel: string | null;
  body: string;
  decision: "approve" | "return" | "note" | null;
  createdAt: string;
  attachments: Attachment[];
}

interface ReviewContext {
  step: {
    id: string;
    state: string;
    output: unknown;
  };
  upstream: UpstreamStep[];
  attachments: Attachment[];
  comments: CommentRow[];
  card: { id: string; type: string; state: string } | null;
}

function AttachmentList({ items }: { items: Attachment[] }) {
  if (items.length === 0) return null;
  return (
    <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12 }}>
      {items.map((a) => (
        <li key={a.id}>
          📎{" "}
          {a.url ? (
            <a
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent)" }}
            >
              {a.filename || a.url}
            </a>
          ) : (
            a.filename || "(file)"
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Shows upstream outputs, this step's output, attachments, and prior comments
 * for any step — open or completed. Sits at the top of every step drawer so
 * an approver/decider can see "what got us here" without leaving the page.
 */
export function ReviewContextPanel({
  workspaceId,
  stepId,
}: {
  workspaceId: string;
  stepId: string;
}) {
  const q = useQuery<ReviewContext, ApiError>({
    queryKey: ["step-review-context", workspaceId, stepId],
    queryFn: () =>
      apiGet<ReviewContext>(
        `/api/workspaces/${workspaceId}/steps/${stepId}/review-context`,
      ),
    enabled: !!stepId,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <section
        data-testid="step-review-context-loading"
        style={{
          margin: "0 0 12px",
          padding: 12,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--bg)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 11, color: "var(--text-3)", letterSpacing: 0.4, textTransform: "uppercase" }}>
          Loading context…
        </div>
        <div style={{ height: 10, background: "var(--bg-soft)", borderRadius: 4, width: "60%" }} />
        <div style={{ height: 10, background: "var(--bg-soft)", borderRadius: 4, width: "90%" }} />
        <div style={{ height: 10, background: "var(--bg-soft)", borderRadius: 4, width: "75%" }} />
      </section>
    );
  }
  if (q.error || !q.data) return null;
  const { upstream, attachments, comments, step } = q.data;

  const nothingToShow =
    upstream.length === 0 &&
    attachments.length === 0 &&
    comments.length === 0 &&
    step.output == null;
  if (nothingToShow) return null;

  return (
    <section
      data-testid="step-review-context"
      style={{
        margin: "0 0 12px",
        padding: 12,
        border: "1px solid var(--border)",
        borderRadius: 8,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {upstream.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "var(--text-3)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            What got us here
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {upstream.map((u) => (
              <details
                key={u.id}
                open={upstream.length <= 2}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: 8,
                  background: "var(--bg-soft)",
                }}
              >
                <summary
                  style={{ cursor: "pointer", fontSize: 13, listStyle: "revert" }}
                >
                  <strong>{u.title}</strong>{" "}
                  <span style={{ color: "var(--text-3)" }}>
                    · {u.type} · {u.state}
                  </span>
                </summary>
                <div style={{ marginTop: 6 }}>
                  <OutputRender output={u.output} />
                  <AttachmentList items={u.attachments} />
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {(step.output != null || attachments.length > 0) && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "var(--text-3)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            This step's output
          </div>
          <OutputRender output={step.output} />
          <AttachmentList items={attachments} />
        </div>
      )}

      {comments.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "var(--text-3)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Comments
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {comments.map((c) => (
              <li
                key={c.id}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: 8,
                  background: "var(--bg-soft)",
                }}
              >
                <div style={{ fontSize: 11, color: "var(--text-3)", display: "flex", gap: 6, alignItems: "center" }}>
                  <span>{c.authorLabel ?? c.authorUserId}</span>
                  {c.decision && (
                    <span
                      style={{
                        background: "var(--bg)",
                        padding: "1px 6px",
                        borderRadius: 3,
                        fontWeight: 600,
                      }}
                    >
                      {c.decision}
                    </span>
                  )}
                  <span>· {new Date(c.createdAt).toLocaleString()}</span>
                </div>
                {hasMarkdown(c.body) ? (
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    <MarkdownText>{c.body}</MarkdownText>
                  </div>
                ) : (
                  <p style={{ margin: "4px 0 0", fontSize: 13, whiteSpace: "pre-wrap" }}>
                    {c.body}
                  </p>
                )}
                <AttachmentList items={c.attachments} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
